import { formatDateRange } from '@trips/shared/format';
import { ITEM_TYPE_LABELS } from '@trips/shared/items';
import type { Item } from '@trips/shared/schema';
import type { TripSummary } from '@trips/shared/trip-model';
import { useMemo, useState } from 'react';
import {
  selectLiveItems,
  selectPlaceLookup,
  selectPrefs,
  selectTripSummaries,
} from '../../data/selectors';
import { useStore } from '../../data/store';
import { back, navigate } from '../../lib/router';
import { IconButton } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { itemIcon } from '../../ui/itemIcon';
import s from './SearchScreen.module.css';

const norm = (v: string) => v.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function SearchScreen() {
  const [q, setQ] = useState('');
  const trips = useStore(selectTripSummaries);
  const items = useStore(selectLiveItems);
  const places = useStore(selectPlaceLookup);
  const prefs = useStore(selectPrefs);
  const tripById = useMemo(() => new Map(trips.map((t) => [t.id, t])), [trips]);

  const results = useMemo(() => {
    const term = norm(q.trim());
    if (term.length < 2) return null;
    const tripHits: TripSummary[] = trips.filter((t) =>
      norm([t.title, ...t.places, ...t.cities.map((c) => c.name)].join(' ')).includes(term),
    );
    const itemHits: Item[] = items
      .filter((i) => {
        const p = places(i.startPlaceId);
        const e = places(i.endPlaceId);
        const hay = [
          i.title,
          i.provider,
          i.confirmation,
          i.notes,
          p?.name,
          p?.city,
          p?.address,
          p?.iata,
          e?.name,
          e?.city,
          e?.iata,
        ]
          .filter(Boolean)
          .join(' ');
        return norm(hay).includes(term);
      })
      .slice(0, 60);
    return { tripHits, itemHits };
  }, [q, trips, items, places]);

  return (
    <div className={s.screen}>
      <div className={s.bar}>
        <IconButton icon="arrow-left" label="Back" onClick={() => back('/')} />
        <label className={s.field}>
          <Icon name="magnifying-glass" size={18} />
          <input
            // biome-ignore lint/a11y/noAutofocus: search opens to type
            autoFocus
            className={s.input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Trips, places, confirmation codes, notes"
            aria-label="Search"
            enterKeyHint="search"
          />
        </label>
      </div>
      {!results ? (
        <EmptyState icon="magnifying-glass" title="Search everything on this device">
          <p>Works offline. Try a city, a hotel, a flight number or a confirmation code.</p>
        </EmptyState>
      ) : results.tripHits.length + results.itemHits.length === 0 ? (
        <EmptyState icon="magnifying-glass" title={`Nothing matches "${q.trim()}"`}>
          <p>Check the spelling, or search for part of a name.</p>
        </EmptyState>
      ) : (
        <div className={s.results}>
          {results.tripHits.length > 0 && (
            <section className={s.group}>
              <h2 className="t-footnote muted">Trips</h2>
              {results.tripHits.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={s.row}
                  onClick={() => navigate(`/trips/${t.id}`)}
                >
                  <Icon name="suitcase-rolling" size={20} />
                  <span className={s.text}>
                    <span className={s.title}>{t.title}</span>
                    <span className="t-footnote muted num">
                      {t.startDate && t.endDate
                        ? formatDateRange(t.startDate, t.endDate, prefs)
                        : ''}
                    </span>
                  </span>
                </button>
              ))}
            </section>
          )}
          {results.itemHits.length > 0 && (
            <section className={s.group}>
              <h2 className="t-footnote muted">Plans</h2>
              {results.itemHits.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  className={s.row}
                  onClick={() =>
                    i.type === 'flight'
                      ? navigate(`/flights/${i.id}`)
                      : i.tripId
                        ? navigate(`/trips/${i.tripId}/plans/${i.id}`, { overlay: true })
                        : undefined
                  }
                >
                  <Icon name={itemIcon(i, i.type)} size={20} />
                  <span className={s.text}>
                    <span className={s.title}>{i.title}</span>
                    <span className="t-footnote muted">
                      {ITEM_TYPE_LABELS[i.type]}
                      {i.tripId && tripById.get(i.tripId)
                        ? `, ${tripById.get(i.tripId)!.title}`
                        : ''}
                      {i.confirmation && norm(i.confirmation).includes(norm(q.trim()))
                        ? `, ${i.confirmation}`
                        : ''}
                    </span>
                  </span>
                </button>
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
